import { useEffect, useMemo, useRef, useState } from 'react';
import { useItemSets, groupNamesBySet, KIND_LABEL } from './collections';
import { folderForSet } from './investments';
import './ModalTheme.css';

/**
 * Folder picker — the one place the app asks "which folder?".
 *
 * Two callers, one question:
 *   • the Inventory tabs, when items are sent to the Vault, so a batch can be
 *     filed the moment it arrives instead of landing unfiled and being sorted
 *     later;
 *   • the Vault itself, to move a position or a unit from one folder to
 *     another — the per-unit select can only pick a folder that already exists,
 *     and this can make one on the spot.
 *
 * Sets
 * ----
 * Most piles worth filing are a pile the game already named: the items came out
 * of one case, collection or capsule. So the picker offers all 421 of them as
 * ready-made folders, and puts the ones the items being filed actually belong
 * to at the top — the common case is one click on a folder nobody had to
 * invent. Picking one reuses the folder that already stands for that set if
 * there is one, which is what keeps a second "Huntsman Collection" from
 * appearing.
 *
 * It never writes anything. Creating a folder is the caller's job, because the
 * folder list lives on settings and only SkinTable owns that; this hands back
 * an existing id, a name to create, or a set, and applyFolderPick in
 * investments.js turns any of the three into a folder id.
 *
 * Props:
 *   folders      – [{ id, name, color, setId }] the folders that exist now
 *   initialId    – folder the items are in already, or null for unfiled
 *   names        – skin names of the items being filed, for the suggestions
 *   title/subtitle/confirmLabel/note – copy, since the callers ask the same
 *                  question for different reasons
 *   variant      – 'zone' | 'classic' skin, see ModalTheme.css
 *   onConfirm    – ({ folderId, newFolderName, set }) => void; one is set
 *   onClose      – () => void
 */

const NEW = '__new__';

// The filter chips over the browse list, in the order the tiers matter to
// someone filing a portfolio. Only kinds actually present get a chip.
const KIND_ORDER = ['collection', 'case', 'capsule', 'souvenir', 'music', 'graffiti', 'pins', 'other'];

export default function FolderPickModal({
  folders = [],
  initialId = null,
  names = [],
  title = 'Move to a folder',
  subtitle = 'Folders are just names for piles of units. An item can sit in one, or in none.',
  confirmLabel = 'Move',
  note = null,
  variant = 'classic',
  onConfirm,
  onClose,
}) {
  // '' is the unfiled pile, NEW is "make one", anything else is a folder id.
  const [pick, setPick] = useState(() => (initialId && folders.some(f => f.id === initialId) ? initialId : ''));
  // A set stands apart from `pick`: it is not a folder yet, and which folder it
  // becomes is decided after the modal closes.
  const [chosenSet, setChosenSet] = useState(null);
  const [name, setName] = useState('');
  const [browse, setBrowse] = useState(false);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  const nameRef = useRef(null);

  const index = useItemSets();

  // Same backdrop rule as the other modals: a click only closes when both the
  // press and the release landed outside the panel.
  const pressedBackdrop = useRef(false);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => { if (pick === NEW) nameRef.current?.focus(); }, [pick]);

  // What the items in hand are actually from. Knives and gloves resolve to
  // nothing — they drop from too many cases for the data to name one — so the
  // count that matched is shown rather than implied.
  const { groups, unknown } = useMemo(
    () => groupNamesBySet(index, names),
    [index, names],
  );

  const kinds = useMemo(() => {
    const present = new Set(index.sets.map(s => s.kind));
    return KIND_ORDER.filter(k => present.has(k));
  }, [index.sets]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return index.sets.filter(s =>
      (kind === 'all' || s.kind === kind) && (!q || s.name.toLowerCase().includes(q)));
  }, [index.sets, query, kind]);

  const naming  = pick === NEW;
  const trimmed = name.trim();
  const ready   = chosenSet ? true : (!naming || trimmed.length > 0);
  const clash   = naming && trimmed.length > 0
    && folders.some(f => f.name.toLowerCase() === trimmed.toLowerCase());

  // The three answers are exclusive, so choosing one clears the others.
  const chooseFolder = (id) => { setPick(id); setChosenSet(null); };
  const chooseSet = (s) => { setChosenSet(s); setPick(''); };

  const apply = () => {
    if (!ready) return;
    if (chosenSet) onConfirm?.({ folderId: null, newFolderName: null, set: chosenSet });
    else if (naming) onConfirm?.({ folderId: null, newFolderName: trimmed, set: null });
    else onConfirm?.({ folderId: pick || null, newFolderName: null, set: null });
    onClose?.();
  };

  // A set that already has a folder is a move into that folder, not a new one,
  // and the button says so rather than promising a duplicate.
  const existingFor = chosenSet && folderForSet(folders, chosenSet);

  return (
    <div className={`mdl-overlay ${variant}`}
      onMouseDown={e => { pressedBackdrop.current = e.target === e.currentTarget; }}
      onClick={e => { if (e.target === e.currentTarget && pressedBackdrop.current) onClose?.(); }}>
      <div className="mdl-panel narrow" role="dialog" aria-modal="true" aria-label={title}>

        <div className="mdl-head">
          <div className="mdl-head-txt">
            <div className="mdl-title">{title}</div>
            <div className="mdl-sub">{subtitle}</div>
          </div>
          <button className="mdl-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="mdl-body">
          <div className="mdl-stack gap">

            {/* ── Where these items came from ───────────────────
                Always drawn when there are items to place, including when the
                answer is "nowhere". A silently absent card reads exactly like
                a broken lookup, which is the one thing it must not do. */}
            {names.length > 0 && (
              <div className="mdl-card">
                {!index.ready ? (
                  <div className="mdl-card-hint">
                    Looking up which collection {names.length === 1 ? 'this came' : 'these came'} from…
                  </div>
                ) : groups.length === 0 ? (
                  <>
                    <div className="mdl-card-title">No collection for these</div>
                    <div className="mdl-card-hint">
                      The game data does not place {names.length === 1 ? 'this item' : 'these items'} in
                      any collection, case or capsule. Knives and gloves drop from several cases each,
                      so there is no single set to name. Pick a folder below, or make one.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mdl-card-title">
                      {groups.length === 1 && unknown === 0
                        ? `These are all from one ${KIND_LABEL[groups[0].set.kind] || 'set'}`
                        : 'Where these items came from'}
                    </div>
                    {/* Green rather than the usual pill: this is the answer the
                        data already worked out, and it should read as the one
                        to take. The set's own icon is what makes it
                        recognisable at a glance. */}
                    <div className="fpm-suggests">
                      {groups.map(g => (
                        <button key={g.set.id} type="button"
                          className={`fpm-suggest${chosenSet?.id === g.set.id ? ' on' : ''}`}
                          onClick={() => chooseSet(g.set)}>
                          {g.set.image
                            ? <img src={g.set.image} alt="" loading="lazy" />
                            : <span className="fpm-coll-blank" />}
                          <span className="fpm-suggest-txt">
                            <span className="fpm-suggest-name">{g.set.name}</span>
                            <span className="fpm-suggest-sub">
                              {g.count === names.length
                                ? (g.count === 1 ? 'this item' : `all ${g.count}`)
                                : `${g.count} of ${names.length}`}
                              {' · '}{KIND_LABEL[g.set.kind] || 'set'}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                    <div className="mdl-card-hint">
                      {unknown > 0
                        ? `${unknown} of these ${unknown === 1 ? 'has' : 'have'} no set in the game data, because knives and gloves drop from several cases each. Filing this way still moves every item you picked.`
                        : 'Filing this way reuses the folder that already stands for it, or makes one named after it.'}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Folders ───────────────────────────────────── */}
            <div className="mdl-card">
              <div className="mdl-card-title">Pick a folder</div>
              <div className="mdl-controls">
                <button type="button" className={`mdl-pill${!chosenSet && pick === '' ? ' on mute' : ''}`}
                  onClick={() => chooseFolder('')}>
                  No folder
                </button>
                {folders.map(f => (
                  <button key={f.id} type="button"
                    className={`mdl-pill${!chosenSet && pick === f.id ? ' on' : ''}`}
                    onClick={() => chooseFolder(f.id)}>
                    {/* Whatever the folder wears in the Vault, so the pill and
                        the folder it files into look like the same thing. */}
                    {f.image ? (
                      <img src={f.image} alt="" loading="lazy" style={{
                        width: 20, height: 15, marginRight: 7,
                        objectFit: 'contain', verticalAlign: 'middle',
                      }} />
                    ) : (
                      <span style={{
                        display: 'inline-block', width: 9, height: 9, marginRight: 7,
                        borderRadius: 999, verticalAlign: 'middle',
                        background: f.color || 'currentColor',
                      }} />
                    )}
                    {f.name}
                  </button>
                ))}
                <button type="button" className={`mdl-pill${naming && !chosenSet ? ' on info' : ''}`}
                  onClick={() => chooseFolder(NEW)}>
                  + New folder
                </button>
              </div>

              {naming && !chosenSet && (
                <div style={{ marginTop: 12 }}>
                  <span className="mdl-label">Name it</span>
                  <input ref={nameRef} className="mdl-input" value={name}
                    placeholder="Knives, Cases, Q4 exit…"
                    onChange={e => setName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }} />
                  {clash && (
                    <div className="mdl-card-hint" style={{ marginTop: 6 }}>
                      A folder called <strong>{trimmed}</strong> already exists. This makes a second
                      one with the same name. Pick the existing folder above instead if that is what
                      you meant.
                    </div>
                  )}
                </div>
              )}

              {!naming && folders.length === 0 && (
                <div className="mdl-card-hint" style={{ marginTop: 10 }}>
                  You have no folders yet. Everything sits in one unfiled pile until you make one.
                </div>
              )}
            </div>

            {/* ── Every set, as a ready-made folder ──────────── */}
            {index.sets.length > 0 && (
              <div className="mdl-card">
                <div className="mdl-card-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span>Or use a collection, case or capsule</span>
                  <button type="button" className="mdl-btn sm" style={{ marginLeft: 'auto' }}
                    onClick={() => setBrowse(b => !b)}>
                    {browse ? 'Hide' : `Browse all ${index.sets.length}`}
                  </button>
                </div>

                {browse && (
                  <>
                    <input className="mdl-input" style={{ marginTop: 10 }} value={query}
                      placeholder="Search by name…"
                      onChange={e => setQuery(e.target.value)} />
                    <div className="mdl-controls" style={{ marginTop: 8 }}>
                      <button type="button" className={`mdl-pill${kind === 'all' ? ' on' : ''}`}
                        onClick={() => setKind('all')}>All</button>
                      {kinds.map(k => (
                        <button key={k} type="button" className={`mdl-pill${kind === k ? ' on' : ''}`}
                          onClick={() => setKind(k)}>
                          {KIND_LABEL[k] || k}
                        </button>
                      ))}
                    </div>
                    <div className="fpm-colls">
                      {matches.length === 0 ? (
                        <div className="mdl-card-hint">Nothing matches that.</div>
                      ) : matches.map(s => (
                        <button key={s.id} type="button"
                          className={`fpm-coll${chosenSet?.id === s.id ? ' on' : ''}`}
                          onClick={() => chooseSet(s)}>
                          {s.image
                            ? <img src={s.image} alt="" loading="lazy" />
                            : <span className="fpm-coll-blank" />}
                          <span className="fpm-coll-name">{s.name}</span>
                          <span className="fpm-coll-n">{s.count} items</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

          </div>
        </div>

        <div className="mdl-foot">
          <span className="mdl-foot-note">
            {chosenSet
              ? (existingFor
                  ? <>Into your existing <strong>{existingFor.name}</strong> folder.</>
                  : <>Makes the folder <strong>{chosenSet.name}</strong>.</>)
              : note}
          </span>
          <button className="mdl-btn" onClick={onClose}>Cancel</button>
          <button className="mdl-btn primary" onClick={apply} disabled={!ready}>
            {!chosenSet && naming ? `${confirmLabel} into "${trimmed || '…'}"` : confirmLabel}
          </button>
        </div>

      </div>
    </div>
  );
}
